/**
 * Panel — fullscreen widget with toolbar + sidebar/content layout
 */

import { createElement } from '../utils/dom.js';
import { EVENTS } from '../core/events.js';

export default class Panel {
  /**
   * @param {HTMLElement} targetElement - The site's search input/button
   * @param {Object} config - Widget config
   * @param {Object} eventBus - EventBus instance
   * @param {Object} stateManager - StateManager instance
   */
  constructor(targetElement, config, eventBus, stateManager) {
    this._target = targetElement;
    this._config = config;
    this._eventBus = eventBus;
    this._stateManager = stateManager;

    this._rootEl = null;
    this._overlayEl = null;
    this._panelEl = null;
    this._toolbarEl = null;
    this._sidebarEl = null;
    this._contentEl = null;
    this._savedBodyOverflow = '';

    this._onKeydown = this._handleKeydown.bind(this);
    this._onOverlayClick = this._handleOverlayClick.bind(this);
  }

  /**
   * Build the panel DOM and insert into document
   */
  render() {
    // Root container (hidden by default — FOUC prevention)
    this._rootEl = createElement('div', 'grb-widget', { style: { display: 'none', zIndex: String(this._config.zIndex) } });

    // Overlay
    this._overlayEl = createElement('div', 'grb-widget--overlay');

    // Panel
    this._panelEl = createElement('div', 'grb-widget--panel');

    // Toolbar — search bar and close button go here
    this._toolbarEl = createElement('div', 'grb-widget--toolbar');

    // Body — sidebar + content
    const bodyEl = createElement('div', 'grb-widget--panel-body');

    // Sidebar (filters mount here)
    this._sidebarEl = createElement('div', 'grb-widget--sidebar');

    // Content area (results, loading, empty, error mount here)
    this._contentEl = createElement('div', 'grb-widget--content');

    bodyEl.appendChild(this._sidebarEl);
    bodyEl.appendChild(this._contentEl);

    this._panelEl.appendChild(this._toolbarEl);
    this._panelEl.appendChild(bodyEl);

    this._rootEl.appendChild(this._overlayEl);
    this._rootEl.appendChild(this._panelEl);

    document.body.appendChild(this._rootEl);

    // Attach listeners
    this._overlayEl.addEventListener('click', this._onOverlayClick);
    document.addEventListener('keydown', this._onKeydown);

    return this._rootEl;
  }

  /**
   * Get the toolbar element where search bar mounts
   */
  getToolbarEl() {
    return this._toolbarEl;
  }

  /**
   * Get the sidebar element where filters mount
   */
  getSidebarEl() {
    return this._sidebarEl;
  }

  /**
   * Get the content element where results mount
   */
  getContentEl() {
    return this._contentEl;
  }

  /**
   * Get the root element
   */
  getRootEl() {
    return this._rootEl;
  }

  /**
   * Add filters label to the left side of toolbar (same width as sidebar)
   */
  addFiltersLabel(i18n) {
    const label = createElement('div', 'grb-widget--toolbar-filters-label');
    label.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>';
    const text = createElement('span', '', {}, [i18n.t('filters.title')]);
    label.appendChild(text);

    this._filtersBadge = createElement('span', 'grb-widget--filters-badge');
    label.appendChild(this._filtersBadge);

    this._toolbarEl.insertBefore(label, this._toolbarEl.firstChild);
  }

  /**
   * Update the active filters count badge
   * @param {number} count
   */
  updateFiltersBadge(count) {
    if (!this._filtersBadge) return;
    if (count > 0) {
      this._filtersBadge.textContent = String(count);
      this._filtersBadge.style.display = '';
    } else {
      this._filtersBadge.textContent = '';
      this._filtersBadge.style.display = 'none';
    }
  }

  /**
   * Add close button to toolbar
   */
  addCloseButton(i18n) {
    const closeBtn = createElement('button', 'grb-widget--close-btn', {
      type: 'button',
      'aria-label': i18n ? i18n.t('panel.close') : 'Close',
    });
    closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
    closeBtn.addEventListener('click', () => this.close());
    this._toolbarEl.appendChild(closeBtn);
  }

  /**
   * Show the panel — make visible after styles loaded
   */
  show() {
    if (this._rootEl) {
      this._rootEl.style.display = '';
    }
  }

  /**
   * Open the panel
   */
  open() {
    if (!this._rootEl) return;

    this._overlayEl.classList.add('grb-widget--visible');
    this._panelEl.classList.add('grb-widget--visible');

    this._savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    this._stateManager.setState({ panelOpen: true });
    this._eventBus.emit(EVENTS.PANEL_OPEN);
  }

  /**
   * Close the panel
   */
  close() {
    if (!this._rootEl) return;

    this._overlayEl.classList.remove('grb-widget--visible');
    this._panelEl.classList.remove('grb-widget--visible');

    document.body.style.overflow = this._savedBodyOverflow;

    this._stateManager.setState({ panelOpen: false });
    this._eventBus.emit(EVENTS.PANEL_CLOSE);
  }

  /**
   * Check if panel is open
   */
  isOpen() {
    return this._panelEl ? this._panelEl.classList.contains('grb-widget--visible') : false;
  }

  /** @private */
  _handleKeydown(e) {
    if (e.key === 'Escape' && this.isOpen()) {
      this.close();
    }
  }

  /** @private */
  _handleOverlayClick() {
    this.close();
  }

  /**
   * Destroy — remove DOM and listeners
   */
  destroy() {
    document.removeEventListener('keydown', this._onKeydown);

    if (this._rootEl && this._rootEl.parentNode) {
      this._rootEl.parentNode.removeChild(this._rootEl);
    }

    if (document.body.style.overflow === 'hidden') {
      document.body.style.overflow = this._savedBodyOverflow;
    }

    this._rootEl = null;
    this._overlayEl = null;
    this._panelEl = null;
    this._toolbarEl = null;
    this._sidebarEl = null;
    this._contentEl = null;
  }
}
